import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ActiveUsersSummary,
  DauTimeSeriesPoint,
  DauByOrgTimeSeriesPoint,
  WauTimeSeriesPoint,
  ActivityByOrg,
  ActivityByEventType,
  OrgListEntry,
  UserRetention,
  FilterOption,
} from '../types';

export type DauChartView = 'trend' | 'by-org';
export type OrgChartView = 'absolute' | 'normalized';
export type MetricsTab = 'organizations' | 'activity';

interface MetricsState {
  summary: ActiveUsersSummary | null;
  dauTimeseries: DauTimeSeriesPoint[];
  dauByOrgTimeseries: DauByOrgTimeSeriesPoint[];
  wauTimeseries: WauTimeSeriesPoint[];
  activityByOrg: ActivityByOrg[];
  activityByEventType: ActivityByEventType[];
  orgList: OrgListEntry[];
  retention: UserRetention | null;
  isLoading: boolean;
  isOrgListLoading: boolean;
  dateRange: {
    start: Date;
    end: Date;
  };
  selectedOrgIds: string[];
  availableOrgs: FilterOption[];
  dauChartView: DauChartView;
  orgChartView: OrgChartView;
  selectedTab: MetricsTab;

  setSummary: (summary: ActiveUsersSummary) => void;
  setDauTimeseries: (data: DauTimeSeriesPoint[]) => void;
  setDauByOrgTimeseries: (data: DauByOrgTimeSeriesPoint[]) => void;
  setWauTimeseries: (data: WauTimeSeriesPoint[]) => void;
  setActivityByOrg: (data: ActivityByOrg[]) => void;
  setActivityByEventType: (data: ActivityByEventType[]) => void;
  setOrgList: (data: OrgListEntry[] | ((prev: OrgListEntry[]) => OrgListEntry[])) => void;
  setRetention: (retention: UserRetention) => void;
  setLoading: (loading: boolean) => void;
  setOrgListLoading: (loading: boolean) => void;
  setDateRange: (start: Date, end: Date) => void;
  setSelectedOrgIds: (orgIds: string[]) => void;
  setAvailableOrgs: (orgs: FilterOption[]) => void;
  setDauChartView: (view: DauChartView) => void;
  setOrgChartView: (view: OrgChartView) => void;
  setSelectedTab: (tab: MetricsTab) => void;
}

// Default to last 30 days
const defaultEnd = new Date();
const defaultStart = new Date();
defaultStart.setDate(defaultStart.getDate() - 30);

export const useMetricsStore = create<MetricsState>()(
  persist(
    (set) => ({
      summary: null,
      dauTimeseries: [],
      dauByOrgTimeseries: [],
      wauTimeseries: [],
      activityByOrg: [],
      activityByEventType: [],
      orgList: [],
      retention: null,
      isLoading: false,
      isOrgListLoading: false,
      dateRange: {
        start: defaultStart,
        end: defaultEnd,
      },
      selectedOrgIds: [],
      availableOrgs: [],
      dauChartView: 'trend',
      orgChartView: 'absolute',
      selectedTab: 'organizations',

      setSummary: (summary) => set({ summary }),
      setDauTimeseries: (dauTimeseries) => set({ dauTimeseries }),
      setDauByOrgTimeseries: (dauByOrgTimeseries) => set({ dauByOrgTimeseries }),
      setWauTimeseries: (wauTimeseries) => set({ wauTimeseries }),
      setActivityByOrg: (activityByOrg) => set({ activityByOrg }),
      setActivityByEventType: (activityByEventType) => set({ activityByEventType }),
      setOrgList: (orgList) =>
        set((state) => ({
          orgList: typeof orgList === 'function' ? orgList(state.orgList) : orgList,
        })),
      setRetention: (retention) => set({ retention }),
      setLoading: (isLoading) => set({ isLoading }),
      setOrgListLoading: (isOrgListLoading) => set({ isOrgListLoading }),
      setDateRange: (start, end) => set({ dateRange: { start, end } }),
      setSelectedOrgIds: (selectedOrgIds) => set({ selectedOrgIds }),
      setAvailableOrgs: (availableOrgs) => set({ availableOrgs }),
      setDauChartView: (dauChartView) => set({ dauChartView }),
      setOrgChartView: (orgChartView) => set({ orgChartView }),
      setSelectedTab: (selectedTab) => set({ selectedTab }),
    }),
    {
      name: 'metrics-tab-storage',
      partialize: (state) => ({ selectedTab: state.selectedTab }),
    }
  )
);
